import { useState, useRef, useCallback, useEffect } from 'react';
import { CHAT_CONFIG } from '../../services/chatConfig';

export interface MessageInputProps {
  onSubmit: (message: string) => void;
  disabled?: boolean;
  initialValue?: string;
}

const CHAR_COUNT_THRESHOLD = 1800;

export function MessageInput({ onSubmit, disabled = false, initialValue = '' }: MessageInputProps) {
  const [value, setValue] = useState(initialValue);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (initialValue) {
      setValue(initialValue);
    }
  }, [initialValue]);

  const charCount = value.length;
  const maxLength = CHAT_CONFIG.maxMessageLength;
  const isOverLimit = charCount > maxLength;
  const showCharCount = charCount >= CHAR_COUNT_THRESHOLD;

  const handleSubmit = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed || isOverLimit || disabled) {
      return;
    }
    onSubmit(trimmed);
    setValue('');
  }, [value, isOverLimit, disabled, onSubmit]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue(e.target.value);
  };

  const canSubmit = value.trim().length > 0 && !isOverLimit && !disabled;

  return (
    <div className="message-input-container">
      <div className="message-input-wrapper">
        <textarea
          ref={textareaRef}
          className="message-input-textarea"
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          aria-label="Type your message"
          placeholder="Ask about cannabis..."
          rows={1}
        />
        <button
          type="button"
          className="message-input-send-button"
          onClick={handleSubmit}
          disabled={!canSubmit}
          aria-label="Send message"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        </button>
      </div>
      {showCharCount && (
        <span
          className={`message-input-char-count${isOverLimit ? ' message-input-char-count--error' : ''}`}
          aria-live="polite"
        >
          {charCount}/{maxLength}
        </span>
      )}
    </div>
  );
}
